import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import FormStepper from '../../Form/FormStepper/FormStepper';
import { useAccreditationApplicationFormComponent } from './hooks/useAccreditationApplicationFormComponent';
import IFormStep from 'interfaces/IFormStep';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingSpinner } from '../../LoadingSpinner';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { ReviewerRole, ReviewStatus } from '../../../graphql/__generated__/graphql';
import { isAuthenticationError } from '../../../utils/auth';

interface Props {
    renewal?: boolean;
}

type EmdrComment = {
    content: string;
    date: string;
};

const ApplicationForm: React.FC<Props> = ({ renewal = false }) => {
    const { accreditationTypeId, applicationId } = useParams<{
        accreditationTypeId?: string;
        applicationId?: string;
    }>();

    const formRef = useRef(null);
    const navigate = useNavigate();
    const notificationContext = useNotificationContext();
    const { models, operations } = useAccreditationApplicationFormComponent(
        applicationId,
        accreditationTypeId,
        renewal
    );
    const [showEmdrComments, setShowEmdrComments] = useState(true);
    const [showSupervisorComments, setShowSupervisorComments] = useState(true);

    const {
        formData,
        currentStep,
        requirements,
        operationResult,
        application: loadedApplication,
    } = models;

    useEffect(() => {
        if (operationResult?.message) {
            notificationContext.notify(
                operationResult.success ? 'info' : 'danger',
                operationResult.message
            );
        }
    }, [operationResult?.message, operationResult?.success, notificationContext.notify]);

    if (!loadedApplication) {
        return <LoadingSpinner />;
    }

    const {
        handleCanSkipToStep,
        handleNextStep,
        handleInputChangeEvent,
        handleInputChange,
        handleSaveProgress,
        handleSetStep,
        handleSubmitApplication,
        handleFetchApplication,
    } = operations;

    const handleSubmit = async () => {
        return handleSubmitApplication()
            .then(() => {
                navigate('/accreditation?formSubmitType=supervisor');
                return this;
            })
            .catch((error) => {
                notificationContext.error(
                    isAuthenticationError(error)
                        ? 'Your session expired before the application could be submitted. Your progress has not been submitted. Please sign in again and retry.'
                        : 'An error occurred submitting the application. Your progress has not been submitted.'
                );
                handleFetchApplication().catch(() => {});
            });
    };

    const renderStep = (Step: React.FC<IFormStep>) => (
        <Step
            formData={formData}
            models={models}
            handleInputChangeEvent={handleInputChangeEvent}
            handleFeedback={notificationContext.notify}
            handleNext={handleNextStep}
            handleInputChange={handleInputChange}
            handleSubmitApplication={handleSubmit}
            handleFetchApplication={handleFetchApplication}
            requirements={requirements}
        />
    );

    const handleSaveButtonClicked = async () => {
        try {
            await handleSaveProgress(formData);
            notificationContext.success('Application saved');
        } catch {
            notificationContext.error('An error occurred saving the application');
        }
    };

    const emdrReview = loadedApplication.reviews?.find(
        (review) => review.reviewerRole === ReviewerRole.Emdr
    );

    const supervisorReviews = loadedApplication.reviews?.filter(
        (review) =>
            review.reviewerRole !== ReviewerRole.Emdr &&
            review.reviewStatus === ReviewStatus.WaitingOnApplicant &&
            !!review.jsonData
    );

    const parseComments = (reviewJsonData: string): (string | EmdrComment)[] => {
        const comments = JSON.parse(reviewJsonData)?.comments;
        return Array.isArray(comments) ? comments : [comments];
    };

    return (
        <>
            <h1 className="fs-1 fw-bold mb-5">
                Apply for {loadedApplication.accreditationType.name}{' '}
                {loadedApplication.isRenewal && 're'}
                accreditation
            </h1>

            {emdrReview &&
                emdrReview.reviewStatus === ReviewStatus.WaitingOnApplicant &&
                emdrReview.jsonData && (
                    <Row>
                        <Col>
                            <Alert variant="danger">
                                <div className="d-flex justify-content-between align-items-center gap-3">
                                    <Alert.Heading className="mb-0">
                                        EMDR Association UK Comments
                                    </Alert.Heading>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => setShowEmdrComments((current) => !current)}
                                        aria-expanded={showEmdrComments}
                                    >
                                        {showEmdrComments ? 'Hide' : 'Show'}
                                    </Button>
                                </div>
                                {showEmdrComments &&
                                    parseComments(emdrReview.jsonData).map(
                                        (comment: string | EmdrComment, i: number) => {
                                            const { content, date } =
                                                typeof comment === 'string'
                                                    ? { content: comment, date: undefined }
                                                    : comment;

                                            return (
                                                <div key={i} className="mt-3">
                                                    <p className="text-muted mb-1">
                                                        {date
                                                            ? new Date(date).toLocaleDateString()
                                                            : 'Unknown date'}
                                                    </p>
                                                    <p
                                                        className="ps-5 mb-0"
                                                        style={{
                                                            whiteSpace: 'pre-wrap',
                                                            wordWrap: 'break-word',
                                                        }}
                                                    >
                                                        {content}
                                                    </p>
                                                </div>
                                            );
                                        }
                                    )}
                            </Alert>
                        </Col>
                    </Row>
                )}

            {supervisorReviews && supervisorReviews.length > 0 && (
                <Row>
                    <Col>
                        <Alert variant="danger">
                            <div className="d-flex justify-content-between align-items-center gap-3">
                                <Alert.Heading className="mb-0">Supervisor Comments</Alert.Heading>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => setShowSupervisorComments((current) => !current)}
                                    aria-expanded={showSupervisorComments}
                                >
                                    {showSupervisorComments ? 'Hide' : 'Show'}
                                </Button>
                            </div>
                            {showSupervisorComments &&
                                supervisorReviews.map((review, i) => {
                                    return (
                                        <div key={i} className="mt-3">
                                            <p className="mb-0">
                                                {JSON.parse(review.jsonData!).comments} -{' '}
                                                {review.reviewer.name}
                                            </p>
                                        </div>
                                    );
                                })}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col>
                    <Form ref={formRef} noValidate>
                        <FormStepper
                            stepNumber={currentStep}
                            renderStep={renderStep}
                            steps={models.stepsConfig}
                            formRef={formRef}
                            handleFeedback={notificationContext.notify}
                            handleCanSkipToStep={handleCanSkipToStep}
                            handleChangeStep={handleSetStep}
                            handleCancel={() => {
                                navigate('/accreditation');
                            }}
                            handleSubmit={(e) => {
                                // The application doesn't use a typical form submit
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            handleSaveProgress={handleSaveButtonClicked}
                            submitted={false}
                            submitting={false}
                            allowSubmit={false}
                            allowSkipStep={!!loadedApplication.accreditationApplicationId}
                            submitText={'Submit and pay'}
                            nextButtonText={'Save and continue'}
                            className={'overflow-x-auto'}
                        ></FormStepper>
                    </Form>
                </Col>
            </Row>
        </>
    );
};

export default ApplicationForm;
